import React from 'react';
import { Box, IconButton, List, ListItem, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { DropZone } from 'components/DropZone';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ExplanationStepFormData } from './ExplanationStepActive';
import DeleteIcon from '@mui/icons-material/Delete';
import { FilePreview } from 'components/FilePreview';
import { filesize } from 'filesize';
import sumBy from 'lodash/sumBy';

interface AttachmentsSectionProps {
  disabled?: boolean
}

export const AttachmentsSection: React.FC<AttachmentsSectionProps> = (props) => {
  const { formatMessage } = useIntl();
  const maxFileSize = Number.parseInt(process.env.REACT_APP_MAX_FILE_SIZE ?? '0');

  const { control, formState } = useFormContext<ExplanationStepFormData>();
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'attachments',
    rules: { validate: (files) => sumBy(files, (x) => x.file.size) < maxFileSize }
  });
  const handleOnDrop = (files: File[]) => {
    files.forEach(file => {
      append({ file });
    });
  };

  const handleRemoveAttachment = (index: number) => {
    remove(index);
  };

  return (
    <Stack gap={2}>
      <Stack>
        <Typography variant='subtitle1' fontWeight={500}>{formatMessage({ id: 'UploadFilesSection.title', defaultMessage: 'Attachments' })}</Typography>
        <Typography variant='body2' color='text.secondary'>{formatMessage({ id: 'UploadFilesSection.description', defaultMessage: 'Upload photos or videos of the article' })}</Typography>
      </Stack>
      <Stack color='text.secondary'>
        <Typography>{formatMessage({ id: 'UploadFilesSection.instruction', defaultMessage: 'In order to properly process your request, please send us:' })}</Typography>
        <Box component="ul" m={0}>
          <li>{formatMessage({ id: 'UploadFilesSection.instructionLine1', defaultMessage: 'Photos of the complete article' })}</li>
          <li>{formatMessage({ id: 'UploadFilesSection.instructionLine2', defaultMessage: 'Photo of the manufacturer\'s label' })}</li>
          <li>{formatMessage({ id: 'UploadFilesSection.instructionLine3', defaultMessage: 'Photo of the problem' })}</li>
        </Box>
      </Stack>
      <Stack>
        {!props.disabled &&
          <DropZone accept={{ 'image/*': [], 'video/*': [] }} onDrop={handleOnDrop} />
        }
        <List>
          {fields.map((attachment, index) => (
            <ListItem key={attachment.id}>
              <Stack direction='row' justifyContent='space-between' flexGrow={1} gap={2}>
                <Stack direction='row' alignItems='center' gap={2} flexGrow={1}>
                  <Stack alignItems='center' justifyContent='center'>
                    <FilePreview file={attachment.file} style={{ height: 40, width: 40, objectFit: 'cover'}} />
                  </Stack>
                  <Stack>
                    <Typography sx={{ wordWrap: 'break-word', overflowWrap: 'anywhere' }}>{attachment.file.name}</Typography>
                    <Typography variant='caption' color='text.secondary'>{filesize(attachment.file.size) as string}</Typography>
                  </Stack>
                </Stack>
                <Stack direction='row' flexGrow={2} justifyContent='end' maxHeight={40}>
                  <IconButton disabled={props.disabled} edge="end" onClick={() => handleRemoveAttachment(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </ListItem>
          ))}
        </List>
        <Stack direction='row' paddingLeft={2} gap={1/4} color='text.secondary'>
          <Typography color={formState.errors.attachments ? 'error' : 'text.secondary'} variant='caption'>{filesize(sumBy(fields, x => x.file.size)) as string}</Typography>
          <Typography variant='caption'>/</Typography>
          <Typography variant='caption'>{filesize(maxFileSize) as string}</Typography>
        </Stack>
        {formState.errors.attachments &&
          <Typography paddingLeft={2} variant='caption' color='error'>{formatMessage({ id: 'AttachmentsSection.FilesTooLarge', defaultMessage: 'Attachments exceed allowed file size' })}</Typography>
        }
      </Stack>
    </Stack>
  );
};